import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Save,
  Loader,
  AlertTriangle,
  CheckCircle,
  Upload,
  FileText,
  Trash2,
} from "lucide-react";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";
import { useUser } from "@clerk/clerk-react";

export default function CreateRequestModal({ onClose, onCreate }) {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const { user } = useUser();
  const userClient = user?.publicMetadata?.client;

  // Add blur effect to header and sidebar when modal opens
  useEffect(() => {
    document.body.classList.add("modal-open");
    
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    severity: "Medium",
    email: "",
    phoneNumber: "",
    tags: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return "🖼️";
      case "zip":
      case "rar":
        return "📦";
      default:
        return "📎";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    if (!formData.description.trim()) {
      setError("Description is required");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Use multipart/form-data endpoint that accepts files
      const submitFormData = new FormData();
      submitFormData.append("Title", formData.title);
      submitFormData.append("Description", formData.description);
      submitFormData.append("CompanyName", userClient?.name);
      submitFormData.append("CreatedBy", user?.fullName);
      submitFormData.append("Severity", formData.severity);
      submitFormData.append("Email", formData.email);
      submitFormData.append("PhoneNumber", formData.phoneNumber);
      submitFormData.append("Tags", formData.tags);

      // Append all selected files
      selectedFiles.forEach((file) => {
        submitFormData.append("files", file);
      });

      const response = await apiClient.post(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_WITH_ATTACHMENTS,
        submitFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const createdRequest = response.data;

      setSuccess(
        selectedFiles.length > 0
          ? `Request created with ${selectedFiles.length} attachment(s)!`
          : "Request created successfully!"
      );

      if (onCreate) {
        onCreate(createdRequest);
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to create request:", err);
      setError(err.response?.data?.message || "Failed to create request");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="request-detail-overlay">
      <div className="create-request-modal">
        {/* Header */}
        <div className="create-request__header">
          <h2 className="create-request__title">Create New Request</h2>
          <button className="request-detail__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="request-detail__message request-detail__message--error">
            <AlertTriangle size={16} />
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="request-detail__message request-detail__message--success">
            <CheckCircle size={16} />
            <span>{success}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="create-request__form">
          <div className="request-form__section">
            <div className="request-form__field">
              <label htmlFor="title">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief title for the request"
                autoFocus
              />
            </div>

            <div className="request-form__field">
              <label htmlFor="description">
                Description <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                placeholder="Detailed description of your request..."
              />
            </div>

            <div className="request-form__row">
              <div className="request-form__field">
                <label htmlFor="severity">Severity</label>
                <select
                  id="severity"
                  name="severity"
                  value={formData.severity}
                  onChange={handleInputChange}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div className="request-form__field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                />
              </div>
            </div>

            <div className="request-form__row">
              <div className="request-form__field">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="+1234567890"
                />
              </div>

              <div className="request-form__field">
                <label htmlFor="tags">Tags</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="vpn, network, security"
                />
              </div>
            </div>

            {/* File Upload Section */}
            <div className="request-form__field">
              <label>Attachments</label>
              <div className="file-upload-zone">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  multiple
                />
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={16} />
                  Choose Files
                </button>
                <span className="file-upload-zone__hint">
                  You can upload multiple files
                </span>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div className="selected-files-list">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="selected-file-item">
                      <span className="selected-file-item__icon">
                        {getFileIcon(file.name)}
                      </span>
                      <div className="selected-file-item__info">
                        <span className="selected-file-item__name">
                          {file.name}
                        </span>
                        <span className="selected-file-item__size">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="selected-file-item__remove"
                        onClick={() => handleRemoveFile(index)}
                        title="Remove file"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="create-request__actions">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Loader size={16} className="spinning" />
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
